import React, { type ChangeEvent, type KeyboardEvent, type MouseEvent } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import type { Brand } from '@flipdish/orgmanagement';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getTranslate } from 'react-localize-redux';
import { FixedSizeList as List } from 'react-window';

import { SWITCH_ORG_ID } from '../hooks/useBrandSelector';
import { renderBrandItem } from '../renderBrandItem';

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  whiteSpace: 'nowrap',
  span: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isCurrent' && prop !== 'isSelected',
})<{ isCurrent?: boolean; isSelected?: boolean }>(({ isCurrent, isSelected }) => ({
  backgroundColor: isCurrent ? '#eaf2ff' : 'transparent',
  '&:hover': {
    backgroundColor: '#eaf2ff',
  },
  ...(isSelected && {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
  }),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const StyledLoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  marginBottom: theme.spacing(1),
}));

const StyledMessageContainer = styled('div')({
  padding: 16,
});

const SearchContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: '#fff',
}));

const ITEM_SIZE = 60;

type Props = {
  /** The brand the user is in, highlighted in the list. */
  currentApp: AppState['currentApp'];
  /** Org the brands belong to, named in the "no brands" message. */
  orgId?: string;
  /** Every brand of the org; the search field only shows once there are more than `maxVisibleItems`. */
  brands: Brand[];
  /** Brands matching the search, in list order. */
  filteredBrands: Brand[];
  loading: boolean;
  /** Keyboard highlight: an index into `filteredBrands`, or their length for the switch org row. -1 for none. */
  selectedIndex: number;
  maxVisibleItems: number;
  onSearch: (value: string) => void;
  /** A brand row or the switch org row (`brandId: SWITCH_ORG_ID`) was chosen. */
  onSelect: (
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
    brand: { brandId?: string } | undefined,
    newTab?: boolean
  ) => void;
};

/** Search field, brand list and "Switch organisation" row. Host it in a menu or a panel. */
export const BrandSelectorMenuContent = ({
  currentApp,
  orgId,
  brands,
  filteredBrands,
  loading,
  selectedIndex,
  maxVisibleItems,
  onSearch,
  onSelect,
}: Props) => {
  const translate = useAppSelector((state) => getTranslate(state.locale));
  const filteredBrandsLength = filteredBrands?.length;
  // Calculate the height of the List dynamically
  const listHeight =
    filteredBrandsLength < maxVisibleItems
      ? filteredBrandsLength * ITEM_SIZE
      : maxVisibleItems * ITEM_SIZE;

  const renderBrandsOrMessage = () => {
    if (!filteredBrandsLength) {
      return (
        <StyledMessageContainer>
          <Typography variant="body1">
            {translate('No_brands_found', { orgId: orgId || '' })}
          </Typography>
        </StyledMessageContainer>
      );
    }
    return (
      <List
        height={listHeight}
        itemCount={filteredBrands?.length}
        itemSize={ITEM_SIZE}
        width="100%"
      >
        {renderBrandItem({
          currentApp,
          selectedIndex,
          filteredBrands,
          handleChange: onSelect,
          translate,
        })}
      </List>
    );
  };

  return (
    <>
      {brands?.length > maxVisibleItems && (
        <>
          <SearchContainer>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder={translate('Search_brands') as string}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onSearch(event.target.value)}
              inputProps={{
                'data-fd': 'brand-selector-search-input',
              }}
            />
          </SearchContainer>
          <Divider />
        </>
      )}
      {loading ? (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      ) : (
        renderBrandsOrMessage()
      )}
      <StyledDivider />
      <StyledMenuItem
        key={SWITCH_ORG_ID}
        onClick={(event) => onSelect(event, { brandId: SWITCH_ORG_ID })}
        data-fd="brand-selector-switch-org"
        isSelected={
          selectedIndex === filteredBrands?.length ||
          (!filteredBrands?.length && selectedIndex === 0)
        }
      >
        <ListItemIcon>
          <SwapHorizIcon />
        </ListItemIcon>
        <StyledListItemText primary={translate('Switch_organisation')} />
      </StyledMenuItem>
    </>
  );
};

export default BrandSelectorMenuContent;
